import React, { useContext } from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { Badge, Kind as BadgeKind, Typography, units } from '@ha/dsl';

import { useIntl } from 'ha/i18n';
import { useLocation } from 'ha/helpers/router';
import FilterLayout from 'ha/components/Filters/FilterLayout';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { SearchFiltersContext } from 'ha/pages/Search/SearchFiltersContext';

import { getNumberOfListings } from '../../selectors';
import SearchFilters from './SearchFilters';
import SearchFiltersTags from './SearchFiltersTags';
import getFilterTagsCount from '../../utils/getFilterTagsCount';

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginLeft: units.rem(1.5),
    marginRight: units.rem(1.5),
  },
  badgeContainer: {
    paddingBottom: units.rem(1.5),
  },
  badge: {
    backgroundColor: theme.palette.orange.main,
    marginLeft: units.rem(0.5),
  },
}));

interface Props {
  onShowResultsClick?: () => void;
  withDateFilter?: boolean;
}

const SearchFiltersButtonContent: React.FC<Props> = ({
  onShowResultsClick,
  withDateFilter,
}) => {
  const { T } = useIntl();
  const classes = useStyles();
  const location = useLocation();
  const { md } = useMediaQuery();

  const { localState, clearFilters } = useContext(SearchFiltersContext);

  const tagsState = React.useMemo(
    () => ({
      ...localState,
      dates: withDateFilter
        ? localState.dates
        : { startDate: null, endDate: null },
      price: localState.price,
    }),
    [localState, withDateFilter],
  );

  const tagsCount = getFilterTagsCount(tagsState);

  const total = useSelector(getNumberOfListings);

  const handleShowResultsClick = React.useCallback(() => {
    if (onShowResultsClick) {
      onShowResultsClick();
    }
  }, [onShowResultsClick]);

  const handleResetClick = React.useCallback(() => {
    clearFilters(withDateFilter ? ['dates'] : undefined);
  }, [withDateFilter, clearFilters]);

  return (
    <FilterLayout
      type={md ? 'desktop' : 'mobile'}
      showResultsButtonText={`${total} ${T('Results')}`}
      onShowResultsClick={handleShowResultsClick}
      resetButtonText={T('cta.clear_all')}
      updateSearchAlert={!md && !!location.query?.updateSearchAlert}
      updateSearchAlertText={T('search_alert_update_button')}
      onResetClick={handleResetClick}
      title={
        !md ? (
          <Badge
            kind={BadgeKind.inline}
            color="secondary"
            className={classes.wrapper}
            badgeClassName={classes.badge}
            badgeContent={tagsCount}
          >
            <Typography.H6>{T('Filters')}</Typography.H6>
          </Badge>
        ) : undefined
      }
    >
      {tagsCount > 0 && (
        <div className={cx(classes.wrapper, classes.badgeContainer)}>
          <SearchFiltersTags withDateFilter={withDateFilter} />
        </div>
      )}
      <SearchFilters withDateFilter={withDateFilter} />
    </FilterLayout>
  );
};

export default SearchFiltersButtonContent;
